import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome'
import { FlashList, ListRenderItemInfo } from '@shopify/flash-list'
import { useRouter } from 'expo-router'
import React from 'react'
import { Dimensions } from 'react-native'
import { ms, ScaledSheet } from 'react-native-size-matters'
import useSWRInfinite from 'swr/infinite'
import { CustomerChatCategoryResource, CustomerChatCategoryResourceCollection } from '~api/types'
import CacheableImage from '~components/CacheableImage'
import ChattaCustomerSearch from '~components/ChattaCustomerSearch'
import Loading from '~components/Loading'
import ModalScreen from '~components/ModalScreen'
import { NavigationItem } from '~components/NavigationItem'
import RenderError from '~components/RenderError'
import { View } from '~components/Themed'
import useColumns from '~hooks/useColumns'
import { Theme } from '~theme'
import { useTheme } from '~theme/ThemeManager'
import opacity from '~utils/opacity'

export default function ChattaCommunityCategoriesScreen(): React.JSX.Element {
  const { theme } = useTheme()
  const styles = getStyles(theme)
  const columns = useColumns()
  const router = useRouter()

  const { data, size, setSize, isValidating } =
    useSWRInfinite<CustomerChatCategoryResourceCollection>((pageIndex, previousPageData) => {
      // First
      if (pageIndex === 0) {
        return `/api/v1/customer/templates/tags`
      }

      // End
      if (previousPageData && previousPageData.links.next !== null) {
        return previousPageData.links.next
      }

      return null
    })

  const categories = (Array.isArray(data) ? data : [])
    .flatMap((o) => o.data)
    .filter((item, index, array) => {
      return array.findIndex((o) => o.id === item.id) === index
    })

  const more =
    size > 0 &&
    data !== undefined &&
    data[size - 1] !== undefined &&
    data[size - 1]?.links.next !== null

  const renderItem = ({ item }: ListRenderItemInfo<CustomerChatCategoryResource>) => {
    const src = item.thumbnail

    return (
      <NavigationItem
        title={item.name}
        onPress={() => router.navigate(`./school/tag/${item.id}`)}
        background={
          src ? (
            <View style={styles.navigationItemContainer}>
              <CacheableImage
                source={{ uri: src }}
                style={[styles.navigationItemImage, styles.navigationItemImageWithoutHeader]}
                contentFit="cover"
                placeholder={
                  <FontAwesomeIcon
                    style={styles.navigationItemPlaceholder}
                    color={theme[400]}
                    size={ms(53)}
                    icon={['fal', 'file-image']}
                  />
                }
              />
              <View
                style={[styles.navigationItemOverlay, styles.navigationItemImageWithoutHeader]}
              />
            </View>
          ) : undefined
        }
        icon={['fal', 'file-image']}
        backgroundContainerStyle={styles.backgroundContainerStyle}
      />
    )
  }

  const renderEmptyList = isValidating ? (
    <View style={styles.emptyContainer}>
      <Loading />
    </View>
  ) : (
    <View style={styles.emptyContainer}>
      <RenderError
        title="Oops, nothing here!"
        description={`No templates have been found.\nWhy not create one and share with the community!`}
      />
    </View>
  )

  return (
    <ModalScreen>
      <ChattaCustomerSearch
        key={`chattacustomercategorywrapper-${columns}`}
        placeholder={
          categories.length === 0 ? (
            renderEmptyList
          ) : (
            <FlashList<CustomerChatCategoryResource>
              key={`chattacommunitycategory-${columns}`}
              contentContainerStyle={styles.listContainer}
              data={categories}
              numColumns={columns}
              renderItem={renderItem}
              keyExtractor={(item) => item.id}
              ItemSeparatorComponent={() => <View style={styles.itemSeparator} />}
              onEndReached={() => more && setSize(size + 1)}
              onEndReachedThreshold={0.25}
            />
          )
        }
      />
    </ModalScreen>
  )
}

const getStyles = (theme: Theme) =>
  ScaledSheet.create({
    listContainer: {
      paddingVertical: '20@mvs',
      paddingHorizontal: '10@ms',
    },
    header: {
      paddingHorizontal: '10@ms',
      paddingBottom: '20@mvs',
      flexDirection: Dimensions.get('window').width < 640 ? 'column-reverse' : 'row',
    },
    emptyContainer: {
      paddingHorizontal: '10@ms',
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
    },
    headerActions: {
      flexGrow: 1,
      alignItems: Dimensions.get('window').width < 640 ? 'center' : 'flex-end',
    },
    headerActionTextStyle: {
      fontWeight: '600',
    },
    subtitle: {
      fontSize: '12@ms0.4',
      fontWeight: '300',
    },
    navigationItemContainer: {
      flex: 1,
      justifyContent: 'center',
      borderTopLeftRadius: '5@ms',
      borderTopRightRadius: '5@ms',
    },
    navigationItemImage: {
      flex: 1,
      resizeMode: 'cover',
    },
    navigationItemImageWithoutHeader: {
      borderTopLeftRadius: '5@ms',
      borderTopRightRadius: '5@ms',
    },
    navigationItemPlaceholder: {
      alignSelf: 'center',
    },
    navigationItemOverlay: {
      flex: 1,
      backgroundColor: opacity(theme[500], 0.3),
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
    createdByChatta: {
      flexDirection: 'row',
      backgroundColor: theme.primary,
      justifyContent: 'center',
      alignItems: 'center',
      borderTopLeftRadius: '5@ms',
      borderTopRightRadius: '5@ms',
      paddingHorizontal: '4@ms',
      paddingVertical: '3@mvs',
    },
    createdByChattaText: {
      fontSize: '9@ms',
      fontWeight: '700',
      color: theme.white,
      marginLeft: '4@ms',
    },
    itemSeparator: {
      height: '16@ms',
    },
    backgroundContainerStyle: {
      borderStartWidth: 0,
      borderTopWidth: 0,
      borderEndWidth: 0,
    },
  })
